/** @jsxImportSource ./simboluri-jsx */
// COTELE — primitivele de desen tehnic, într-un singur loc (
// „cel puțin cotele le vreau și pe celelalte tipuri de elemente
// reprezentate").
//
// Până acum fiecare secțiune scria dimensiunile ca text lângă figură
// („30×70 cm"). O cotă adevărată e altceva: o linie cu repere oblice la
// capete, martori punctați până la muchia cotată și valoarea pe linie. Le
// folosesc toate cele cinci secțiuni pe care le comută `DesenElement`
// (grindă, stâlp, perete, ochi de placă, ax de fundație) plus vederea cotată
// a stâlpului — deci trăiesc aici, nu în pagina fiecărui pas.
//
// CONVENȚII (fixate o dată, ca desenele să arate la fel):
//   · linia de cotă e mereu CERNEALA (--ink). Accentul se folosește doar
//     pentru mărimea DESPRE CARE e desenul, roșul pentru armătură,
//     chihlimbarul pentru infrastructură.
//   · reperele sunt oblice la 45°, lungi de 8 px, ca în planșe.
//   · martorii sunt punctați 3-3 și se opresc la 2 px de linia de cotă.
//   · metrii se scriu cu virgulă și două zecimale, centimetrii întregi —
//     v. `mFmt` / `cmFmt`.

export const COTA_LINIE = "#232323";
export const COTA_MARTOR = "#c8c8c8";
const REPER = 4; // jumătatea reperului oblic (deci 8 px cap la cap)

/** Metri, în formatul planșelor: virgulă zecimală, două zecimale. */
export function mFmt(v: number | null | undefined, zec = 2): string {
  if (v == null || !isFinite(v)) return "—";
  return v.toLocaleString("ro-RO", {
    minimumFractionDigits: zec,
    maximumFractionDigits: zec,
  });
}

/** Centimetri întregi. */
export function cmFmt(v: number | null | undefined): string {
  if (v == null || !isFinite(v)) return "—";
  return Math.round(v).toLocaleString("ro-RO");
}

function reperOblic(x: number, y: number): string {
  return `M ${x - REPER},${y + REPER} L ${x + REPER},${y - REPER}`;
}

/** Cotă VERTICALĂ la abscisa `x`, între ordonatele `y1` și `y2` (px).
 *  `martorX` = abscisa muchiei cotate; dacă e dată, se trag martorii. */
export function CotaV({
  x,
  y1,
  y2,
  text,
  martorX,
  culoare = COTA_LINIE,
  size = 10,
  laDreapta = false,
}: {
  x: number;
  y1: number;
  y2: number;
  text: string;
  martorX?: number;
  culoare?: string;
  size?: number;
  // eticheta pe partea dreaptă a liniei (implicit stânga)
  laDreapta?: boolean;
}) {
  const ym = (y1 + y2) / 2;
  const tx = laDreapta ? x + size / 2 + 1 : x - size / 2 - 1;
  const d =
    `M ${x},${y1} L ${x},${y2} ` + reperOblic(x, y1) + " " + reperOblic(x, y2);
  // COTĂ SCURTĂ (grosimea unei plăci, o acoperire): textul rotit ar fi de
  // câteva ori mai lung decât cota și ar tăia raiul vecin — măsurat pe
  // proiectul 2, unde „placă 0,15" trecea peste martorii cotei grinzii. Ca în
  // planșe, la cotele mici valoarea se scrie ORIZONTAL, lateral.
  const scurta = Math.abs(y2 - y1) < 3.4 * size;
  return (
    <g>
      {martorX != null && (
        <>
          {/* martorul vine DINSPRE muchie și se oprește la 2 px de linia de
              cotă — deci semnul depinde de partea pe care stă muchia */}
          <line
            x1={martorX} y1={y1}
            x2={x + (martorX > x ? 2 : -2)} y2={y1}
            stroke={COTA_MARTOR} strokeWidth={0.6} strokeDasharray="3 3"
          />
          <line
            x1={martorX} y1={y2}
            x2={x + (martorX > x ? 2 : -2)} y2={y2}
            stroke={COTA_MARTOR} strokeWidth={0.6} strokeDasharray="3 3"
          />
        </>
      )}
      <path d={d} fill="none" stroke={culoare} strokeWidth={0.8} />
      <text
        x={scurta ? (laDreapta ? x + 7 : x - 7) : tx}
        y={scurta ? ym + size * 0.36 : ym}
        transform={scurta ? undefined : `rotate(-90 ${tx} ${ym})`}
        fontFamily="var(--mono)" fontSize={size} fontWeight={500}
        fill={culoare}
        textAnchor={scurta ? (laDreapta ? "start" : "end") : "middle"}
      >
        {text}
      </text>
    </g>
  );
}

/** Cotă ORIZONTALĂ la ordonata `y`, între abscisele `x1` și `x2` (px).
 *  `martorY` = ordonata muchiei cotate; dacă e dată, se trag martorii. */
export function CotaH({
  x1,
  x2,
  y,
  text,
  martorY,
  culoare = COTA_LINIE,
  size = 10,
  deasupra = false,
}: {
  x1: number;
  x2: number;
  y: number;
  text: string;
  martorY?: number;
  culoare?: string;
  size?: number;
  // eticheta deasupra liniei (implicit dedesubt)
  deasupra?: boolean;
}) {
  const d =
    `M ${x1},${y} L ${x2},${y} ` + reperOblic(x1, y) + " " + reperOblic(x2, y);
  return (
    <g>
      {martorY != null && (
        <>
          <line
            x1={x1} y1={martorY}
            x2={x1} y2={y + (martorY > y ? 2 : -2)}
            stroke={COTA_MARTOR} strokeWidth={0.6} strokeDasharray="3 3"
          />
          <line
            x1={x2} y1={martorY}
            x2={x2} y2={y + (martorY > y ? 2 : -2)}
            stroke={COTA_MARTOR} strokeWidth={0.6} strokeDasharray="3 3"
          />
        </>
      )}
      <path d={d} fill="none" stroke={culoare} strokeWidth={0.8} />
      <text
        x={(x1 + x2) / 2} y={deasupra ? y - size * 0.55 : y + size * 1.25}
        fontFamily="var(--mono)" fontSize={size} fontWeight={500}
        fill={culoare} textAnchor="middle"
      >
        {text}
      </text>
    </g>
  );
}

/** Linia terenului sistematizat, cu hașura de umplutură. */
export function LinieTeren({
  x1,
  x2,
  y,
  eticheta,
}: {
  x1: number;
  x2: number;
  y: number;
  eticheta?: string;
}) {
  const pasi: number[] = [];
  for (let x = x1 + 4; x < x2 - 6; x += 13) pasi.push(x);
  return (
    <g>
      <line x1={x1} y1={y} x2={x2} y2={y} stroke="#c1c1c1" strokeWidth={1} />
      {pasi.map((x, i) => (
        <line key={i} x1={x} y1={y} x2={x + 7} y2={y - 7}
              stroke="#c1c1c1" strokeWidth={0.7} />
      ))}
      {eticheta && (
        <text x={x1 + 2} y={y - 11} fontFamily="var(--mono)" fontSize={9.5}
              fill="var(--mut)">
          {eticheta}
        </text>
      )}
    </g>
  );
}

/** Hașura unei benzi de placă (diagonale la 45°). */
export function HasuraPlaca({
  x,
  y,
  w,
  h,
  pas = 11,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  pas?: number;
}) {
  // se pornește cu o înălțime de bandă ÎNAINTE de marginea din stânga, ca
  // diagonalele să acopere și colțul din stânga-sus
  const linii: number[] = [];
  for (let xa = x - h; xa <= x + w; xa += pas) linii.push(xa);
  return (
    <g>
      {linii.map((xa, i) => {
        // segmentul la 45°, tăiat de marginile benzii
        const x1 = Math.max(xa, x);
        const y1 = y + h - (x1 - xa);
        const x2 = Math.min(xa + h, x + w);
        const y2 = y + h - (x2 - xa);
        if (x2 <= x1 || y1 < y) return null;
        return (
          <line key={i} x1={x1} y1={y1} x2={x2} y2={Math.max(y2, y)}
                stroke="#a8adb4" strokeWidth={0.55} />
        );
      })}
    </g>
  );
}

/** Legenda vocabularului, pentru desenele mari. */
export function LegendaCote({ randuri }: { randuri: [string, string][] }) {
  return (
    <div style={{
      display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11,
      color: "var(--mut)",
    }}>
      {randuri.map(([culoare, text], i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{
            width: 11, height: 11, borderRadius: 3, background: culoare,
            border: "1px solid rgba(30, 36, 36,.18)", display: "inline-block",
          }} />
          {text}
        </span>
      ))}
    </div>
  );
}
